import { authApp as callAuthAppService, API_URL } from "../services/api";
import { toast } from "react-hot-toast";
import { authentication as teamsAuthentication } from "@microsoft/teams-js";

// API_URL should be accessible here, e.g., from an env variable or a config file
// For simplicity, assuming it's globally available or configured elsewhere.
// If not, you might need to pass it or import it.
const openAuthPopup = (url, title, w, h) => {
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : window.screen.width;
    const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : window.screen.height;

    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft;
    const top = (height - h) / 2 / systemZoom + dualScreenTop;

    const newWindow = window.open(url, title,
        `scrollbars=yes, width=${w / systemZoom}, height=${h / systemZoom}, top=${top}, left=${left}`);

    if (newWindow) {
        if (window.focus) newWindow.focus();
    } else {
        toast.error("Popup was blocked. Please allow popups for this site.");
    }
    return newWindow;
};

export const authenticateApp = async (appName, accessToken, onAuthSuccess, updateAuthApps, isTeamsInitialized, options = {}) => {
    // `accessToken` here is user_google_id for your backend
    // `options` can contain { envType: 'SANDBOX' | 'PRODUCTION' } for QuickBooks
    try {
        if (isTeamsInitialized) {
            // --- TEAMS FLOW ---
            console.log(`Teams: Initiating auth for ${appName}`);
            const teamsAppAuthStartUrl = `${window.location.origin}/teams-app-auth-start.html` +
                `?appName=${encodeURIComponent(appName)}` +
                `&accessToken=${encodeURIComponent(accessToken)}` + // This is user_google_id
                `&backendAuthAppBaseUrl=${encodeURIComponent(API_URL + "/authApp")}`; // Base URL for /authApp/{appname}

            // Append envType for QuickBooks
            if (options.envType) {
                teamsAppAuthStartUrl += `&envType=${encodeURIComponent(options.envType)}`;
            }

            try {
                const resultString = await teamsAuthentication.authenticate({
                    url: teamsAppAuthStartUrl,
                    width: 600,
                    height: 700,
                    isExternal: false,
                });

                // resultString is expected to be JSON: {status: 'success', appName: '...'} or {status: 'error', ...}
                if (resultString) {
                    const resultData = JSON.parse(resultString);
                    if (resultData.status === 'success') {
                        console.log(`Teams: Successfully authenticated ${resultData.appName}`);
                        if (updateAuthApps) updateAuthApps(resultData.appName); // Pass appName if needed for specific update
                        if (onAuthSuccess) onAuthSuccess(resultData.appName);    // Pass appName if needed
                    } else { // status === 'error'
                        toast.error(resultData.error || `Failed to authenticate ${resultData.appName} via Teams.`);
                        // Optionally call updateAuthApps/onAuthSuccess if UI needs refresh even on error
                        if (updateAuthApps) updateAuthApps(resultData.appName);
                        if (onAuthSuccess) onAuthSuccess(resultData.appName);
                    }
                } else {
                    toast.error(`Authentication for ${appName} via Teams completed with no result.`);
                }
            } catch (error) { // Catch errors from teamsAuthentication.authenticate()
                const errorMessage = (error instanceof Error) ? error.message : String(error);
                toast.error(`Teams Auth Error for ${appName}: ${errorMessage}`);
                // Optionally call updateAuthApps/onAuthSuccess
                if (updateAuthApps) updateAuthApps();
                if (onAuthSuccess) onAuthSuccess();
            }

        } else {
            // --- REVISED WEB FLOW ---
            console.log(`Web: Initiating auth for ${appName}`);

            // 1. Construct the URL for our new intermediate page.
            let webAppAuthStartUrl = `${window.location.origin}/web-app-auth-start.html` +
                `?appName=${encodeURIComponent(appName)}` +
                `&accessToken=${encodeURIComponent(accessToken)}` + // Pass the user's token
                `&backendAuthAppBaseUrl=${encodeURIComponent(API_URL + "/authApp")}`;

            // Append envType for QuickBooks
            if (options.envType) {
                webAppAuthStartUrl += `&envType=${encodeURIComponent(options.envType)}`;
            }
            // 2. Open the popup IMMEDIATELY.
            const popup = openAuthPopup(webAppAuthStartUrl, `${appName} Authentication`, 600, 700);
            console.log("Web: Auth popup opened:", popup);
            if (!popup) {
                // The openAuthPopup function already shows a toast error.
                return;
            }

            // 3. The existing message listener is still PERFECT. It will handle two types of messages:
            //    - A 'success' message from `web-app-auth-start.html` if the app was already authenticated.
            //    - The final 'success' or 'error' message from your backend's `/oauth_callback` endpoint.
            const receiveMessage = (event) => {
                const expectedOrigin = new URL(API_URL).origin;
                // Loosening origin check slightly for flexibility between pages, but be cautious in production.
                if (event.origin !== window.location.origin && event.origin !== expectedOrigin) {
                    console.warn("Message from unexpected origin:", event.origin);
                    return;
                }

                if (!event.data || (typeof event.data.status === 'undefined')) {
                    return; // Ignore invalid messages
                }

                // console.log("Web: Received message:", event.data);
                const { status, appName: eventAppName, error } = event.data;

                // Handle both success and error messages
                if (status === 'success') {
                    // console.log(`Web: Successfully processed authentication for ${eventAppName}`);
                    toast.success(`Successfully connected ${eventAppName}!`);
                    if (updateAuthApps) updateAuthApps(eventAppName);
                    if (onAuthSuccess) onAuthSuccess(eventAppName);
                } else if (status === 'error') {
                    toast.error(error || `Failed to connect ${eventAppName}.`);
                    if (updateAuthApps) updateAuthApps(eventAppName); // Still might need to refresh UI state
                }

                // Clean up
                if (popup && !popup.closed) {
                    popup.close();
                }
                window.removeEventListener('message', receiveMessage);
            };

            window.addEventListener('message', receiveMessage, false);

            // Also, monitor if the user closes the popup manually
            const poller = setInterval(() => {
                if (popup && popup.closed) {
                    clearInterval(poller);
                    console.log("Web: Auth popup was closed.");
                    // Remove the listener if the popup is closed without a message
                    // to prevent it from lingering.
                    window.removeEventListener('message', receiveMessage);
                }
            }, 500);
        }

    } catch (error) { // Catch errors from the initial callAuthAppService in web flow
        console.error("Error initiating authentication process:", error);
        toast.error(error.message || "Failed to start authentication. Please try again.");
        // Ensure loading states are reset if necessary, though not managed in this function directly
    }
};